import React, { useState, useLayoutEffect, useEffect } from "react";
import { Helmet } from 'react-helmet';

import "./BooksStyles.css";

import axios from "axios";

// Form Imports
import Select from 'react-select';
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from 'primereact/inputtext';

// Animation Imports
import { Fade } from 'react-reveal';

// Select Options
import { StateOptions } from "../components/Apply/DropdownOptions/StateOptions";
import { GenderOptions } from "../components/Apply/DropdownOptions/GenderOptions";
import { ReadingLevelOptions } from "../components/Apply/DropdownOptions/ReadingLevelOptions";

// Application Helper object
import ApplicationHelper from "../data/ApplicationHelper";
import ErrorMessage from "../components/General/ErrorMessage";
import BookApplicationSubmitted from "../components/Books/BookApplicationSubmitted";

const APPLICATION_STATES = {
    CONSENT_DISPLAYING: 0,
    APPLICATION_DISPLAYING: 1,
    SUBMITTED_DISPLAYING: 2,
};

function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}

export default function Books(props) {
    // Width / Height
    const [width, height] = useWindowSize();

    const userData = props.userData;

    // Front end controls
    const [applyButtonPressed, setApplyButtonPressed] = useState(false);
    const [applicationSubmitted, setApplicationSubmitted] = useState(false);
    const [displaying, setDisplaying] = useState(APPLICATION_STATES.CONSENT_DISPLAYING);

    const [yourInfoComplete, setYourInfoComplete] = useState(false);
    const [aboutReaderComplete, setAboutReaderComplete] = useState(false);

    // Error Handling
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // General Info
    const [firstName, setFirstName] = useState(userData ? userData.firstName : "");
    const [lastName, setLastName] = useState(userData ? userData.lastName : "");
    const [email, setEmail] = useState(userData ? userData.email : "");

    // Address Section
    const [streetAddress, setStreetAddress] = useState("");
    const [streetAddress2, setStreetAddress2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState(null);
    const [zip, setZip] = useState("");

    // Reader Information
    const [readerFirstName, setReaderFirstName] = useState("");
    const [readerLastName, setReaderLastName] = useState("");
    const [readerGender, setReaderGender] = useState("");
    const [readingLevel, setReadingLevel] = useState("");
    const [readerDetails, setReaderDetails] = useState("");

    // Information for user
    const [token, setToken] = useState("");

    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            fontWeight: state.isSelected ? "bold" : "normal",
            fontSize: width <= 1260 ? "1.2rem" : "1.4rem",
            color: "#2d3436",
            backgroundColor: "#fff",
            "&:hover": {
                backgroundColor: "#f0f0f0",
            },
        }),
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? "#2d3436" : "#fff",
            margin: "12px 0 12px",
            height: width <= 1260 ? "48px" : "55px",
        }),
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const formData = new FormData();
        formData.append('firstName', firstName);
        formData.append('lastName', lastName);
        formData.append('email', email);
        formData.append('streetAddress', streetAddress);
        formData.append('streetAddress2', streetAddress2);
        formData.append('city', city);
        formData.append('state', state.value);
        formData.append('ZIP', zip);
        formData.append('readerFirstName', readerFirstName);
        formData.append('readerLastName', readerLastName);
        formData.append('readerGender', readerGender.value);
        formData.append('readingLevel', readingLevel.value);
        formData.append('readerDetails', readerDetails);

        axios.post('/api/book-application', formData, { withCredentials: true, })
            .then((res) => {
                if (res.data.status) {
                    setToken(res.data.token);
                    setApplicationSubmitted(true);
                } else {
                    setError(true);
                    setErrorMessage(res.data.message);
                }
            })
    }

    useEffect(() => {
        document.title = "Books | Hearts Unite";
    }, []);


    // Handle front end display state
    useEffect(() => {
        if (applyButtonPressed) {
            if (applicationSubmitted) {
                setDisplaying(APPLICATION_STATES.SUBMITTED_DISPLAYING);
            } else {
                setDisplaying(APPLICATION_STATES.APPLICATION_DISPLAYING);
            }
        } else {
            setDisplaying(APPLICATION_STATES.CONSENT_DISPLAYING);
        }
    }, [applyButtonPressed, applicationSubmitted]);


    // Handle yourInfoComplete state
    useEffect(() => {
        if (state !== null) {
            if (ApplicationHelper.verifyFirstName(firstName) && ApplicationHelper.verifyLastName(lastName) &&
                ApplicationHelper.verifyEmail(email) &&
                ApplicationHelper.verifyStreetAddress(streetAddress) && ApplicationHelper.verifyCity(city) &&
                state.value.length > 0 && ApplicationHelper.verifyZip(zip)) {
                setYourInfoComplete(true);
            } else {
                setYourInfoComplete(false);
            }
        } else {
            setYourInfoComplete(false);
        }

    }, [firstName, lastName, email, streetAddress, city, zip, state]);

    // Handle aboutReaderComplete state
    useEffect(() => {
        if (readingLevel !== null && readerGender !== null) {
            if (ApplicationHelper.verifyFirstName(readerFirstName) && ApplicationHelper.verifyLastName(readerLastName)) {
                setAboutReaderComplete(true);
            } else {
                setAboutReaderComplete(false);
            }
        } else {
            setAboutReaderComplete(false);
        }


    }, [readerFirstName, readerLastName, readerGender, readingLevel]);

    const renderYourInfoComplete = () => {
        if (yourInfoComplete) {
            return <h2 className="completed section-status header">Section Completed!</h2>
        }
        return <h2 className="incomplete section-status header">Section Incomplete</h2>
    }

    const renderAboutReaderComplete = () => {
        if (aboutReaderComplete) {
            return <h2 className="completed section-status header">Section Completed!</h2>
        }
        return <h2 className="incomplete section-status header">Section Incomplete</h2>
    }

    const renderApplicationCompleted = () => {
        if (yourInfoComplete && aboutReaderComplete) {
            return <h2 className="completed section-status header">Application Completed!</h2>
        }
        return <h2 className="incomplete section-status header">Application Incomplete</h2>
    }

    const renderApplicationSubmitButton = () => {
        if (yourInfoComplete && aboutReaderComplete) {
            return <button className="apply-submit-button approved-button" onClick={handleSubmit}>Submit Application</button>
        }

        return (
            <button className="apply-submit-button denied-button" onClick={() => {
                setError(true);
                setErrorMessage("Please Complete Application Before Submitting!");
            }}>Unable to Submit</button>
        )
    }


    const renderDisplaying = () => {

        switch (displaying) {
            case APPLICATION_STATES.CONSENT_DISPLAYING: {
                return (
                    <div className="consent-section">
                        <div className="books-text">
                            <p>Hearts Unite to Give Support is partnered with Scholastic to help give books to those in need.</p>
                            <p>Simply provide us information about your young learner and we will help provide you with books that can help them along their literacy journey.</p>
                            <p>Click below to apply for books to help your young reader in their literacy journey!</p>
                        </div>
                        <button className="next-section-button" onClick={() => setApplyButtonPressed(true)}>Click to Apply</button>
                    </div>

                )
            }

            case APPLICATION_STATES.APPLICATION_DISPLAYING: {
                return (
                    <>
                        <div className="application-section">
                            <h1 className="header">Your Information</h1>
                            {renderYourInfoComplete()}
                            <div className="section-items">
                                <div className="section-item">
                                    <label for="firstName">
                                        First Name: {!ApplicationHelper.verifyFirstName(firstName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                        <InputText id='firstName' value={firstName} onChange={(event) => {
                                            if (ApplicationHelper.filterNumbersAndSymbols(event.target.value)) {
                                                setFirstName(event.target.value);
                                            }
                                        }} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="lastName">
                                        Last Name: {!ApplicationHelper.verifyLastName(lastName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                        <InputText id='lastName' value={lastName} onChange={(event) => {
                                            if (ApplicationHelper.filterNumbersAndSymbols(event.target.value)) {
                                                setLastName(event.target.value);
                                            }
                                        }} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="email">
                                        Email: {ApplicationHelper.verifyEmail(email) ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                        <InputText id='email' value={email} onChange={(event) => setEmail(event.target.value)} />
                                    </label>
                                </div>
                            </div>


                            <div className="section-items">

                                <div className="section-item">
                                    <label for="streetAddress">
                                        Street Address: {!ApplicationHelper.verifyStreetAddress(streetAddress) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}

                                        <InputText id='streetAddress' value={streetAddress} onChange={(event) => setStreetAddress(event.target.value)} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="streetAddress2">
                                        Street Address 2:

                                        <InputText id='streetAddress2' value={streetAddress2} onChange={(event) => setStreetAddress2(event.target.value)} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="city">
                                        City: {!ApplicationHelper.verifyCity(city) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}

                                        <InputText id='city' value={city} onChange={(event) => setCity(event.target.value)} />
                                    </label>
                                </div>
                            </div>
                            <div className="two-item-section">
                                <div className="section-item">
                                    <label>
                                        State: {state ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                        <Select
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition={'fixed'}
                                            value={state}
                                            onChange={(option) => setState(option)} options={StateOptions} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="zip">
                                        ZIP: {!ApplicationHelper.verifyZip(zip) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}

                                        <InputText id='zip' value={zip} onChange={(event) => {
                                            if (ApplicationHelper.verifyIsNumber(event.target.value)) {
                                                setZip(event.target.value);
                                            }
                                        }} />
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="application-section">
                            <h1 className="header">About Your Reader</h1>
                            {renderAboutReaderComplete()}
                            <div className="section-items">
                                <div className="section-item">
                                    <label for="firstName">
                                        First Name: {!ApplicationHelper.verifyFirstName(readerFirstName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                        <InputText id='firstName' value={readerFirstName} onChange={(event) => {
                                            if (ApplicationHelper.filterNumbersAndSymbols(event.target.value)) {
                                                setReaderFirstName(event.target.value);
                                            }
                                        }} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label for="lastName">
                                        Last Name: {!ApplicationHelper.verifyLastName(readerLastName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                        <InputText id='lastName' value={readerLastName} onChange={(event) => {
                                            if (ApplicationHelper.filterNumbersAndSymbols(event.target.value)) {
                                                setReaderLastName(event.target.value);
                                            }
                                        }} />
                                    </label>
                                </div>

                                <div className="section-item">
                                    <label>
                                        Gender: {readerGender ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                        <Select
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition={'fixed'}
                                            value={readerGender}
                                            onChange={(option) => setReaderGender(option)} options={GenderOptions} />
                                    </label>
                                </div>
                            </div>

                            <div className="section-item">
                                <label>
                                    Reading Level: {readingLevel ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                    <Select
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition={'fixed'}
                                        value={readingLevel}
                                        onChange={(option) => setReadingLevel(option)} options={ReadingLevelOptions} />
                                </label>

                                <h2 className="header application-reason-header">Your Readers Interests</h2>
                                <p>Let us know some information about your young reader so that we can best tailor the selection of books we send to them.</p>
                                <div>
                                    <label for="readerDetails">
                                        ({1500 - readerDetails.length} Characters Remaining):
                                    </label>
                                </div>

                                <div>
                                    <InputTextarea autoResize={true} rows={5} cols={30}
                                        id="readerDetails" value={readerDetails} onChange={(event) => { setReaderDetails(event.target.value) }} />
                                </div>
                            </div>


                        </div>

                        <div className="application-section">
                            <h1 className="header">Submit Your Application</h1>
                            {renderApplicationCompleted()}
                            <h1 className="header">Disclaimer: </h1>
                            <div className="disclaimer-list">
                                <p>1. Our processing times can be up to 21 days, please try to include all relevant information to help ensure a smooth application process.</p>
                                <p>2. The individual applying should be the guardian of the household, under their own name.</p>
                                <p>3. By submitting this application you are consenting to us contacting you using the information provided.</p>
                            </div>

                            <ErrorMessage error={error} message={errorMessage} />

                            {renderApplicationSubmitButton()}
                        </div>
                    </>
                )
            }

            case APPLICATION_STATES.SUBMITTED_DISPLAYING: {
                return <BookApplicationSubmitted name={firstName} token={token} email={email} />
            }
        }
    }

    return (
        <>
            <Helmet>
                <title>Books | Hearts Unite</title>
                {/* Limits User Zoom */}
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
                <meta name="description" content="Partnered with Scholastic Hearts Unite to Give Support is helping give books to those in need." />
                <meta property="og:title" content="Books | Hearts Unite" />
                <meta property="og:image" content="/images/hugs-logo.png" />
            </Helmet>
            <div className="books-container">
                <div className="page-header">
                    <h1 className='header'>Apply for Books</h1>
                </div>

                {renderDisplaying()}

            </div>

        </>
    )
}
